import { groupMoneyValue, subtractMoneyValues } from '@/shared/design-system';
import { getCurrencySymbol } from '@/utils/numbers/formatters';

const isDecimalString = (value: string) => /^\d+(\.\d+)?$/.test(value);

const isAllZeros = (digits: string) => /^0*$/.test(digits);

/** "5000.00" → "5000"; "0.01" is left alone. A zero fraction is noise on a delta. */
const trimZeroFraction = (magnitude: string) => {
  const [integer, fraction] = magnitude.split('.');
  return fraction === undefined || isAllZeros(fraction) ? integer : magnitude;
};

/**
 * "-$5,000 vs pledged" — the hint both confirm dialogs show when the amount that
 * arrived differs from the amount pledged, which is accepted rather than
 * blocked. Returns null when there is nothing to say.
 *
 * The subtraction runs on the decimal strings themselves. Going through a JS
 * number would defeat the point of holding these amounts as strings, and
 * `formatMoney`'s whole-number output would render a genuine one-unit-of-
 * currency shortfall as "$0" — a mismatch reported as no mismatch. The sign
 * sits outside the symbol because `formatMoney` puts it inside, which reads
 * wrong here.
 */
export const formatReceivedDelta = (
  received: string,
  pledged: string,
  denomination: string,
): string | null => {
  if (!isDecimalString(received) || !isDecimalString(pledged)) return null;

  const { negative, magnitude } = subtractMoneyValues(received, pledged);
  const trimmed = trimZeroFraction(magnitude);
  if (isAllZeros(trimmed.replace('.', ''))) return null;

  const sign = negative ? '-' : '+';

  return `${sign}${getCurrencySymbol(denomination)}${groupMoneyValue(trimmed)} vs pledged`;
};
